import { InfoDialog, Notification } from "@/components"
import { Grid, Divider as MuiDivider, Typography, Link, Chip } from "@material-ui/core"
import React from "react"
import ApiService from "@/services/Api"
import FileDownload from "js-file-download"
import { Alert } from "@material-ui/lab"
import { letalkLinks } from "@/utils/link"
import { colors } from "@/styles/colors"
import { ContactsImportType, ImportContactsHistoryType } from "@/protocols/clientCatalogImportProtocol"

interface ImportContactsReportProps {
	openDialog: boolean
	setOpenDialog: (openDialog: boolean) => void,
	importContactsHistory?: ImportContactsHistoryType
}

const ImportContactsReport = (props: ImportContactsReportProps) => {
	const { openDialog, setOpenDialog, importContactsHistory } = props
	const importId = props.importContactsHistory?.id ? `#${props.importContactsHistory?.id}` : ""
	const importType = (props.importContactsHistory?.importType ? `${props.importContactsHistory?.importType}` : "") as ContactsImportType

	const importTypeToMaterialChips: Record<ContactsImportType, JSX.Element> = {
		text: <Chip
			size="small"
			label="Por texto"
			style={{ backgroundColor: colors.purple[100], width: "120px" }} />,
		file: <Chip
			size="small"
			label="Por arquivo"
			style={{ backgroundColor: colors.blue[100], width: "120px" }} />
	}

	const downloadImportContactsReport = async () => {
		try {
			const { data } = await ApiService.get(`/clients/contacts/import-from-file/${importContactsHistory?.id}/download-report`)

			FileDownload(data, "contatos_nao_importados.csv")
		} catch (error) {
			Notification.error({ message: "Houve um erro ao tentar baixar o relatório de importação de contatos." })
		}
	}

	return <InfoDialog
		title={
			<Grid container spacing={1}>
				<Grid item>
					{`RELATÓRIO DA IMPORTAÇÃO ${importId} `}
				</Grid>
				<Grid item>
					{importTypeToMaterialChips[importType]}
				</Grid>
			</Grid>
		}
		openDialog={openDialog}
		onClose={() => setOpenDialog(false)}
		maxWidth="md"
	>
		{
			importContactsHistory?.status === "error" && <Grid item>
				<Alert icon={false} severity="error">
					Encontramos um erro na sua planilha, <Link href={letalkLinks.wikiHowToImportContacts} target="_blank"><b>clique aqui</b></Link> e acesse o modelo.
				</Alert>
			</Grid>
		}
		<Grid
			container
			spacing={5}
			direction="column"
		>
			<Grid
				style={{ height: "250px" }}
				item
				alignItems="center"
				direction="row"
				xs={12}
			>
				<Grid
					container
					style={{ height: "100%" }}
				>
					<Grid
						item
						justifyContent="center"
						alignContent="center"
						xs={3}
					>
						<Grid
							container
							direction="column"
							alignItems="center"
						>
							<Grid item>
								<Typography style={{ fontSize: "2.5em", fontWeight: "bold", color: colors.gray[700] }} variant="h3" align="center">
									{importContactsHistory?.consolidatedData?.totalContacts || 0}
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant="subtitle2" align="center">
									CONTATOS ENCONTRADOS NO ARQUIVO ENVIADO
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<MuiDivider variant="middle" style={{ background: colors.green[600], width: "5px", opacity: 0.5 }} orientation="vertical" />
					</Grid>
					<Grid
						item
						xs={3}
					>
						<Grid
							container
							alignContent="flex-start"
							direction="column"
							spacing={3}
						>
							<Grid item>
								<Typography variant="h2">
								IMPORTADOS
								</Typography>
							</Grid>
							<Grid item>
								<Grid container
									style={{ height: "100%" }}
									direction="column"
								>
									<Grid item>
										<Typography variant="h4">
											{importContactsHistory?.consolidatedData?.totalNewContacts || 0}
										</Typography>
									</Grid>
									<Grid item>
										<Typography variant="subtitle2">
										NOVOS
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Grid container
									style={{ height: "100%" }}
									direction="column"
								>
									<Grid item>
										<Typography variant="h4">
											{importContactsHistory?.consolidatedData?.totalContactsReplaced || 0}
										</Typography>
									</Grid>
									<Grid item>
										<Typography variant="subtitle2">
										ATUALIZADOS
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<MuiDivider variant="middle" style={{ background: colors.red[400], width: "5px", opacity: 0.5 }} orientation="vertical" />
					</Grid>
					<Grid
						item
						xs={3}
					>
						<Grid
							container
							alignContent="flex-start"
							direction="column"
							spacing={3}
						>
							<Grid item>
								<Typography variant="h2">
								NÃO IMPORTADOS
								</Typography>
							</Grid>
							<Grid item>
								<Grid
									container
									direction="column"
								>
									<Grid item>
										<Typography variant="h4">
											{importContactsHistory?.contactsNotImported?.filter(contact => contact.status !== "duplicate_ignored").length || 0}
										</Typography>
									</Grid>
									<Grid item>
										<Typography variant="subtitle2">
											NÚMEROS INVÁLIDOS
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Grid
									container
									direction="column"
								>
									<Grid item>
										<Typography variant="h4">
											{importContactsHistory?.contactsNotImported?.filter(contact => contact.status === "duplicate_ignored").length || 0}
										</Typography>
									</Grid>
									<Grid item>
										<Typography variant="subtitle2">
											IGNORADOS, JÁ EXISTIA NA LETALK
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<Link
									style={{ cursor: "pointer" }}
									underline="always"
									color="inherit"
									onClick={downloadImportContactsReport}
								>
								Faça o download do CSV com os contatos não importados
								</Link>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<Alert icon={false} severity="warning">
				Lembrete: para que seja possível adicionar tags, enviar mensagens, ou adicionar notas nos contatos importados, os contatos precisam existir no whatsapp. Entenda mais sobre esta regra {""}
					<Link
						href={letalkLinks.wikiWhatHappenAfterImportContacts}
						target="_BLANK"
						variant="h2"
					>
					clicando aqui
					</Link>.
				</Alert>
			</Grid>
		</Grid>
	</InfoDialog>
}

export default ImportContactsReport
